import { motion } from 'framer-motion';
import ButtonCircle from '@/components/ButtonCircle';
import ChevronDown from '@/icons/ChevronDown';
import { commonTransition } from '@/components/Header/Header.animations';
import { useEffect, useRef } from 'react';
import FocusTrap from 'focus-trap-react';
import type { MobileMenuPanelProps } from './MobileMenu.types';

const MobileMenuPanel: React.FC<MobileMenuPanelProps> = ({
  activePage,
  backText = 'Back',
  contactUrl,
  findStationUrl,
  isFirst,
  isLast,
  modifier,
  pages,
  onBackClick,
  onLinkClick,
}) => {
  const navBack = useRef(null);

  const hasChildren = activePage?.children && activePage.children.length > 0;

  const panelVariants = {
    open: { x: 0 },
    closed: { x: '100%' },
  };

  useEffect(() => {
    navBack.current?.focus();
  }, [pages, modifier]);

  return (
    <motion.nav
      className={`mobile-menu__nav mobile-menu__nav--${modifier}`}
      initial="closed"
      animate="open"
      exit="closed"
      variants={panelVariants}
      transition={commonTransition}
    >
      <FocusTrap>
        <div className="mobile-menu__container" tabIndex={0}>
          {onBackClick && (
            <button
              type="button"
              className="mobile-menu__back-button"
              onClick={onBackClick}
              ref={navBack}
            >
              <ChevronDown
                className="mobile-menu__back-icon"
                fill="currentColor"
                width={8}
              />
              {backText}
            </button>
          )}
          {activePage && (
            <a className="mobile-menu__nav-heading" href={activePage.url}>
              {activePage.title}
            </a>
          )}

          {hasChildren && (
            <ul className="mobile-menu__list">
              {activePage.children.map((item, i) => {
                if (!item) return;
                const { id, title, url, isNewWindow, linkingMode } = item;
                const hasChildren = item?.children && item.children.length > 0;

                return (
                  <li className="mobile-menu__list-item" key={i}>
                    {(isLast || !hasChildren) && (
                      <a
                        className={`mobile-menu__list-link mobile-menu__list-link--${linkingMode}`}
                        href={url}
                        target={isNewWindow && '_blank'}
                      >
                        {title}
                      </a>
                    )}

                    {!isLast && hasChildren && (
                      <button
                        type="button"
                        className="mobile-menu__list-button"
                        onClick={() => onLinkClick(id)}
                      >
                        {title}
                        <ChevronDown
                          className="mobile-menu__list-icon"
                          fill="currentColor"
                          width={12}
                        />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {isFirst && (
            <div className="mobile-menu__actions">
              {findStationUrl && findStationUrl !== '/' ? (
                <a
                  className="mobile-menu__station-button button button--primary"
                  href={findStationUrl}
                >
                  Find a station
                  <ButtonCircle arrowHeight={13} arrowWidth={17} />
                </a>
              ) : null}
              {contactUrl && contactUrl !== '/' ? (
                <a className="mobile-menu__contact-link" href={contactUrl}>
                  Contact us
                </a>
              ) : null}
            </div>
          )}
        </div>
      </FocusTrap>
    </motion.nav>
  );
};

export default MobileMenuPanel;
